import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/Spinner';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    ReportSourceProcedureStatus,
    type ReportPipelineSourceStatus,
} from '@/types/report';
import { SourceStatus } from '@/types/source';
import { isSourceAnalyzingPhaseActive } from '../../utils/procedureProgress';

interface ProcedureStatusRowProps {
    source: ReportPipelineSourceStatus;
}

export function ProcedureStatusRow({ source }: ProcedureStatusRowProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const name = source.sourceName;

    const hasError =
        source.sourceStatus === SourceStatus.ERROR ||
        source.procedureStatus === ReportSourceProcedureStatus.ERROR;

    if (hasError) {
        const errorKey =
            source.procedureStatus === ReportSourceProcedureStatus.ERROR
                ? 'detail.procedureProgress.errorExtracting'
                : 'detail.procedureProgress.errorAnalyzing';

        return (
            <li className="flex flex-row items-center gap-2 py-1.5">
                <WarningCircleIcon
                    className="size-3.5 shrink-0 text-grey-300"
                    weight="regular"
                    aria-hidden
                />
                <span className="text-sm text-grey-200">
                    {t(errorKey, { name })}
                </span>
            </li>
        );
    }

    if (source.procedureStatus === ReportSourceProcedureStatus.PROCESSING) {
        return (
            <li className="flex flex-row items-center gap-2 py-1.5">
                <span className="flex size-[14px] shrink-0 items-center justify-center">
                    <Spinner variant="light" size={14} />
                </span>
                <span className="text-sm text-grey-200">
                    {t('detail.procedureProgress.extractingEvents', {
                        name,
                    })}
                </span>
            </li>
        );
    }

    if (source.procedureStatus === ReportSourceProcedureStatus.COMPLETED) {
        return (
            <li className="flex flex-row items-center gap-2 py-1.5">
                <CheckCircleIcon
                    className="size-3.5 shrink-0 text-grey-300"
                    weight="regular"
                    aria-hidden
                />
                <span className="text-sm text-grey-200">
                    {t('detail.procedureProgress.eventsExtracted', {
                        name,
                    })}
                </span>
            </li>
        );
    }

    if (isSourceAnalyzingPhaseActive(source.sourceStatus)) {
        return (
            <li className="flex flex-row items-center gap-2 py-1.5">
                <span className="flex size-[14px] shrink-0 items-center justify-center">
                    <Spinner variant="light" size={14} />
                </span>
                <span className="text-sm text-grey-200">
                    {t('detail.procedureProgress.analyzingSource', { name })}
                </span>
            </li>
        );
    }

    if (source.sourceStatus === SourceStatus.COMPLETED) {
        return (
            <li className="flex flex-row items-center gap-2 py-1.5">
                <CheckCircleIcon
                    className="size-3.5 shrink-0 text-grey-300"
                    weight="regular"
                    aria-hidden
                />
                <span className="text-sm text-grey-200">
                    {t('detail.procedureProgress.analyzingComplete', { name })}
                </span>
            </li>
        );
    }

    return null;
}
